import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { getNotificationsList } from "services/api/notifications/getNotifications";
import { getNotification } from "services/api/notifications/getNotification";
import { getNotificationRecipients } from "services/api/notifications/getNotificationRecipients";
import {
  NOTIFICATIONS_KEY,
  NOTIFICATION_KEY,
  NOTIFICATION_RECIPIENTS_KEY,
} from "constants/TanStackKeys";
import {
  getNotificationPollingInterval,
  getNotificationsListPollingInterval,
} from "helpers/notificationDelivery";

// `pollingEnabled` pauses the refresh, e.g. while a modal covers the list
export const useNotificationsQueries = (status, { pollingEnabled = true } = {}) => {
  return useInfiniteQuery({
    queryKey: [NOTIFICATIONS_KEY, status],
    queryFn: ({ pageParam = 1 }) =>
      getNotificationsList(status),
    initialPageParam: 1,
    getNextPageParam: () => undefined,
    retry: false,
    // Refresh while any send is in progress
    refetchInterval: (query) =>
      pollingEnabled &&
      getNotificationsListPollingInterval(query.state.data?.pages?.[0]),
  });
};

export const useNotificationQuery = (id) => {
  return useQuery({
    queryKey: [NOTIFICATION_KEY, id],
    queryFn: () => getNotification(id),
    enabled: !!id,
    retry: false,
    refetchInterval: (query) =>
      getNotificationPollingInterval(query.state.data),
  });
};

// `notification` is the detail returned by useNotificationQuery: it decides
// whether the recipients exist (stats.total > 0) and the polling interval.
export const useNotificationRecipientsQuery = (
  notificationId,
  filters,
  notification
) => {
  return useQuery({
    queryKey: [NOTIFICATION_RECIPIENTS_KEY, notificationId, filters],
    queryFn: () => getNotificationRecipients(notificationId, filters),
    enabled: !!notificationId && notification?.stats?.total > 0,
    retry: false,
    placeholderData: keepPreviousData,
    refetchInterval: getNotificationPollingInterval(notification),
  });
};
